import { Box, Flex, Heading, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { api } from '../api';
import { StateBadge, StatusBadge } from '../components/Badges';
import { OverviewHost } from '../types';

function formatLastSeen(iso: string | null): string {
  if (!iso) {
    return 'never';
  }
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 90) {
    return `${seconds}s ago`;
  }
  if (seconds < 90 * 60) {
    return `${Math.round(seconds / 60)}m ago`;
  }
  return new Date(iso).toLocaleString();
}

function TlsText({ host }: { host: OverviewHost }) {
  if (host.tlsDaysRemaining === null) {
    return (
      <Text fontSize='sm' color='gray.500'>
        TLS: {host.tlsError ?? 'unknown'}
      </Text>
    );
  }
  const color = host.tlsDaysRemaining < 14 ? 'red.500' : host.tlsDaysRemaining < 30 ? 'orange.500' : 'gray.500';
  return (
    <Text fontSize='sm' color={color}>
      TLS expires in {host.tlsDaysRemaining} days
    </Text>
  );
}

function HostCard({ host }: { host: OverviewHost }) {
  return (
    <RouterLink to={`/hosts/${encodeURIComponent(host.name)}`}>
      <Box
        borderWidth='1px'
        borderColor='card.border'
        bg='card.bg'
        borderRadius='lg'
        p={4}
        _hover={{ borderColor: 'primary.solid' }}
        h='100%'
      >
        <Flex align='center' gap={2} mb={2}>
          <Heading size='sm' flex={1}>
            {host.name}
          </Heading>
          <StateBadge state={host.state} />
          <StatusBadge status={host.status} />
        </Flex>
        <Text fontSize='sm' color='gray.500' mb={1}>
          {host.baseUrl}
        </Text>
        {host.version ? (
          <Text fontSize='sm' mb={1}>
            Version: <b>{host.version}</b>
          </Text>
        ) : null}
        <Text fontSize='sm' mb={1}>
          Sites: {host.siteNames.length > 0 ? host.siteNames.join(', ') : 'unknown'}
        </Text>
        <TlsText host={host} />
        <Text fontSize='xs' color='gray.500' mt={2}>
          Last seen: {formatLastSeen(host.lastSeenAt)}
        </Text>
      </Box>
    </RouterLink>
  );
}

export default function Overview() {
  const { data, isLoading, error } = useQuery({ queryKey: ['overview'], queryFn: api.overview });

  if (isLoading) {
    return <Spinner />;
  }
  if (error) {
    return <Text color='red.500'>Failed to load fleet overview: {(error as Error).message}</Text>;
  }
  const hosts = data?.hosts ?? [];
  return (
    <Flex direction='column' gap={4}>
      <Heading size='xl'>Fleet overview</Heading>
      {hosts.length === 0 ? (
        <Text color='gray.500'>No hosts registered yet. Add your first host on the Manage hosts page.</Text>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={4}>
          {hosts.map(host => (
            <HostCard key={host.name} host={host} />
          ))}
        </SimpleGrid>
      )}
    </Flex>
  );
}
